<template>
    <div v-if="isVisible" class="launcher">
        <div aria-live="polite" aria-atomic="true">
            <BaseNotification v-if="status" :theme="status.theme" :text="status.text" class="status" @dismiss="status = null" />
        </div>

        <BasePopover
            v-model:open="open"
            as-child
            modal
            side="top"
            align="end"
            :side-offset="8"
            :collision-padding="16"
            content-class="w-88 max-w-[calc(100vw-2rem)] max-h-(--reka-popover-content-available-height) overflow-y-auto p-4">
            <template #trigger>
                <BaseButton
                    theme="neutral"
                    class="trigger"
                    :title="showCategories ? 'Demo tools' : title"
                    :aria-label="showCategories ? 'Open demo tools' : `Open ${title.toLowerCase()}`">
                    <i class="bi bi-magic" aria-hidden="true"></i>
                </BaseButton>
            </template>
            <template #content>
                <div v-if="showCategories" class="flex flex-col gap-4">
                    <h2 v-if="panel === 'home'" ref="homeHeading" class="body-small-bold text-ink" tabindex="-1">Demo tools</h2>
                    <template v-if="panel === 'home'">
                        <BaseButton v-if="onboardingEnabled" theme="neutral" size="sm" @click="selectPanel('onboarding')">Onboarding tools</BaseButton>
                        <BaseButton v-if="scope !== 'global'" theme="neutral" size="sm" @click="selectPanel('page')">{{ categoryLabel }}</BaseButton>
                    </template>
                    <template v-else>
                        <BaseButton ref="backButton" theme="neutral" size="sm" aria-label="Back to demo tools" @click="selectPanel('home')">
                            <i class="bi bi-chevron-left" aria-hidden="true"></i>
                            Demo tools
                        </BaseButton>
                        <OnboardingDemoTools v-if="panel === 'onboarding'" @close="open = false" />
                        <slot v-else />
                    </template>
                </div>
                <slot v-else />
            </template>
        </BasePopover>
    </div>
</template>

<script lang="ts">
import type { BaseNotificationTheme } from "~vue/components/BaseNotification.vue";

export interface DemoToolsStatus {
    theme: BaseNotificationTheme;
    text: string;
}
</script>

<script setup lang="ts">
import type { UserDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePopover from "~vue/components/BasePopover.vue";
import OnboardingDemoTools from "~vue/DevTools/OnboardingDemoTools.vue";
import { activeDemoToolsLauncher, type DemoToolsScope, registerDemoToolsLauncher } from "~vue/stores/demoToolsPresenceStore";
import { computed, inject, nextTick, type Ref, ref, useTemplateRef, watch } from "vue";

const { title, scope = "page" } = defineProps<{
    title: string;
    scope?: DemoToolsScope;
}>();
const open = defineModel<boolean>("open", { default: false });

const userDetails = inject<Ref<UserDetails>>("user_details");
const status = ref<DemoToolsStatus | null>(null);

const id = Symbol("demo-tools-launcher");
const isStaff = computed(() => userDetails?.value.is_staff === true);
const isVisible = computed(() => isStaff.value && activeDemoToolsLauncher.value === id);
const onboardingEnabled = computed(() => userDetails?.value.calendar_first_onboarding_tools_enabled === true);
const showCategories = computed(() => onboardingEnabled.value || scope === "global");
const categoryLabel = computed(() => title.replace(" demo tools", " tools"));
// A page/hub-scoped launcher is opened for its own tools specifically, so it should open
// straight to them rather than making the operator pick "<category label>" off a home
// screen every time — even when onboarding tools also happen to be enabled for them.
// Only the global launcher (with no single category of its own) defaults to home.
const defaultPanel = scope === "global" ? "home" : "page";
const panel = ref<"home" | "onboarding" | "page">(defaultPanel);
const homeHeading = useTemplateRef("homeHeading");
const backButton = useTemplateRef("backButton");

watch(
    isStaff,
    (visible, _previous, onCleanup) => {
        if (visible) {
            onCleanup(registerDemoToolsLauncher(id, scope));
        }
    },
    { immediate: true },
);

watch([open, isVisible], ([isOpen, visible]) => {
    if (!isOpen || !visible) {
        open.value = false;
        panel.value = defaultPanel;
    }
});

async function selectPanel(value: typeof panel.value): Promise<void> {
    panel.value = value;
    await nextTick();
    if (value === "home") {
        homeHeading.value?.focus();
    } else {
        backButton.value?.$el?.focus();
    }
}

function finish(result: DemoToolsStatus | null): void {
    status.value = result;
    open.value = false;
}

defineExpose({ finish });
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    /* --mobile-nav-total-height is set on :root at every width, so the md
       override has to be an explicit media query that comes after it. */
    .launcher {
        @apply fixed right-4 z-20 flex max-w-[calc(100vw-2rem)] flex-col items-end gap-3 md:right-6;
        bottom: calc(var(--mobile-nav-total-height, 0px) + 1rem);

        @media (width >= theme(--breakpoint-md)) {
            bottom: 1.5rem;
        }
    }

    .status {
        @apply max-w-sm shadow-lg;
    }

    .trigger {
        @apply inline-flex size-10 items-center justify-center rounded-full p-0 opacity-70 shadow-lg transition-opacity hover:opacity-100 focus-visible:opacity-100;
    }
}
</style>
